import { ArrowRight, Mail, MessageCircle, Layers, FileText, Megaphone, LineChart, MousePointerClick, MailOpen, Send } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import { Container, Reveal, SectionHeader } from "./common";

const channels = [
    [Mail, "Email Marketing"],
    [Layers, "Email Sequences"],
    [MessageCircle, "WhatsApp Marketing"],
    [FileText, "Templates"],
    [Megaphone, "Campaigns"],
    [LineChart, "Analytics"],
];

const weekly = [
    { d: "Mon", sent: 380, opened: 290 },
    { d: "Tue", sent: 420, opened: 315 },
    { d: "Wed", sent: 390, opened: 268 },
    { d: "Thu", sent: 460, opened: 352 },
    { d: "Fri", sent: 410, opened: 301 },
    { d: "Sat", sent: 220, opened: 146 },
    { d: "Sun", sent: 170, opened: 110 },
];

const metrics = [
    { icon: Send, label: "Sent", value: "2,450" },
    { icon: MailOpen, label: "Opened", value: "1,782" },
    { icon: MousePointerClick, label: "Clicked", value: "532" },
    { icon: LineChart, label: "Open Rate", value: "72.7%" },
];

const Engage = () => (
    <section id="engage" data-testid="kryvo-engage-section" className="bg-white py-20 lg:py-28">
        <Container>
            <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.15fr] lg:gap-16">
                <div>
                    <SectionHeader
                        align="left"
                        eyebrow="Kryvo Engage"
                        title="Engage customers across every channel."
                        sub="Turn your CRM data into meaningful customer conversations — campaigns, sequences and templates that reach people where they actually respond."
                    />
                    <div className="mt-8 grid grid-cols-2 gap-3">
                        {channels.map(([Icon, label], i) => (
                            <Reveal key={label} delay={i * 0.05}>
                                <div className="flex items-center gap-3 rounded-xl border border-[#E5E7EB] bg-white px-4 py-3.5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#16A34A]/30 hover:shadow-md">
                                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#F0FDF4]">
                                        <Icon className="h-4 w-4 text-[#16A34A]" />
                                    </span>
                                    <span className="text-sm font-semibold text-[#111827]">{label}</span>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <a
                            href="#pricing"
                            data-testid="engage-explore-link"
                            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#111827] px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg"
                        >
                            Explore Kryvo Engage
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </a>
                    </Reveal>
                </div>

                {/* Campaign analytics dashboard */}
                <Reveal delay={0.15}>
                    <div data-testid="kryvo-engage-dashboard-mockup" className="overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-xl shadow-slate-900/[0.06]">
                        <div className="flex items-center justify-between border-b border-[#F1F5F9] px-5 py-3.5">
                            <div>
                                <p className="font-display text-sm font-bold text-[#111827]">Monsoon Offer Campaign</p>
                                <p className="text-[10px] text-[#94A3B8]">Email + WhatsApp · Launched Jul 14</p>
                            </div>
                            <span className="rounded-full bg-[#F0FDF4] px-2.5 py-1 text-[10px] font-bold text-[#15803D]">Running</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-4">
                            {metrics.map((m) => (
                                <div key={m.label} className="rounded-xl border border-[#F1F5F9] bg-[#FBFDFC] p-3">
                                    <m.icon className="h-3.5 w-3.5 text-[#16A34A]" />
                                    <p className="mt-2 font-display text-lg font-extrabold leading-none text-[#111827]">{m.value}</p>
                                    <p className="mt-1 text-[10px] font-medium text-[#64748B]">{m.label}</p>
                                </div>
                            ))}
                        </div>
                        <div className="px-5 pb-5">
                            <div className="rounded-xl border border-[#F1F5F9] bg-[#FBFDFC] p-4">
                                <div className="mb-2 flex items-center justify-between">
                                    <p className="text-[11px] font-semibold text-[#111827]">Daily engagement</p>
                                    <div className="flex items-center gap-3 text-[9px] font-semibold text-[#64748B]">
                                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#16A34A]" /> Opened</span>
                                        <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[#BBF7D0]" /> Sent</span>
                                    </div>
                                </div>
                                <div className="h-[130px]">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={weekly} margin={{ top: 4, right: 0, left: 0, bottom: 0 }} barGap={3}>
                                            <XAxis dataKey="d" tick={{ fontSize: 9, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                                            <Tooltip
                                                cursor={{ fill: "#F0FDF4" }}
                                                contentStyle={{ fontSize: 11, borderRadius: 10, border: "1px solid #E5E7EB" }}
                                            />
                                            <Bar dataKey="sent" fill="#BBF7D0" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="opened" fill="#16A34A" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </Container>
    </section>
);

export default Engage;
